// AI Insights share card — paints a branded 1200x675 image with the Canvas 2D API
// (no html2canvas dependency; avoids its breakage on Chakra/oklch colors) and
// shares it via the Web Share API, falling back to a file download.

const CARD_W = 1200;
const CARD_H = 675;
const PAD = 64;
const SCALE = 2; // high-DPI backing store for crisp output

const COLORS = {
  bg: '#121212',
  panel: '#1d1d1d',
  orange: '#f7931a',
  white: '#ffffff',
  label: '#f7931a',
  muted: '#999999',
  faint: '#555555',
  green: '#27c93f',
  red: '#ff5f56',
  blue: '#4a96d4',
  gray: '#cccccc'
};

const SANS = "'Inter', 'Helvetica Neue', Arial, sans-serif";
const MONO = "'Ubuntu Mono', 'Courier New', monospace";

const sentimentColor = (sentiment) => {
  const s = (sentiment || '').toLowerCase();
  if (s === 'bullish') return COLORS.green;
  if (s === 'bearish') return COLORS.red;
  return COLORS.muted;
};

const scoreColor = (score) => {
  const n = Number(score);
  if (n >= 7) return COLORS.green;
  if (n >= 4) return COLORS.orange;
  return COLORS.red;
};

// Draw a rounded rect (ctx.roundRect isn't universal across all targets)
const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Wrap text to a max width, returning the lines (capped at maxLines with ellipsis)
const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = (text || '').split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  // Ellipsize the final line if content remains
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = `${last}…`;
  }
  return lines;
};

/**
 * Render the share card to a canvas element.
 * payload: { headline, sentiment, marketScore, sections:[{label,status,color}],
 *            stats:[{label,value}], blockHeight, dateStr, provider }
 */
export const renderShareCardCanvas = (payload) => {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_W * SCALE;
  canvas.height = CARD_H * SCALE;
  const ctx = canvas.getContext('2d');
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'alphabetic';

  // Background
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  // Orange top hairline
  ctx.fillStyle = COLORS.orange;
  ctx.fillRect(0, 0, CARD_W, 6);

  // ---- Header ----
  let x = PAD;
  const headerY = 70;
  // Brand mark: two gray squares + one orange square (mirrors the modal header)
  const sq = 30;
  ctx.fillStyle = '#4d4d4d';
  roundRect(ctx, x, headerY - sq, sq, sq, 4); ctx.fill();
  roundRect(ctx, x + sq + 6, headerY - sq, sq, sq, 4); ctx.fill();
  ctx.fillStyle = COLORS.orange;
  roundRect(ctx, x + (sq + 6) * 2, headerY - sq, sq, sq, 4); ctx.fill();

  const textX = x + (sq + 6) * 3 + 14;
  ctx.fillStyle = COLORS.white;
  ctx.font = `700 30px ${SANS}`;
  ctx.fillText('TIMECHAIN STATS AI', textX, headerY - 6);
  ctx.fillStyle = COLORS.label;
  ctx.font = `700 14px ${SANS}`;
  ctx.fillText('BITCOIN NETWORK ANALYSIS', textX, headerY + 16);

  // Sentiment pill + market score (right aligned)
  ctx.textAlign = 'right';
  let rightX = CARD_W - PAD;
  if (payload.marketScore != null) {
    ctx.fillStyle = scoreColor(payload.marketScore);
    ctx.font = `700 30px ${MONO}`;
    const scoreStr = `${payload.marketScore}/10`;
    ctx.fillText(scoreStr, rightX, headerY - 4);
    const scoreW = ctx.measureText(scoreStr).width;
    ctx.fillStyle = COLORS.muted;
    ctx.font = `700 12px ${SANS}`;
    ctx.fillText('SCORE', rightX, headerY + 16);
    rightX -= Math.max(scoreW, 60) + 28;
  }
  if (payload.sentiment) {
    const sColor = sentimentColor(payload.sentiment);
    ctx.font = `700 18px ${SANS}`;
    const label = payload.sentiment.toUpperCase();
    const w = ctx.measureText(label).width + 28;
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    roundRect(ctx, rightX - w, headerY - 30, w, 34, 4); ctx.fill();
    ctx.fillStyle = sColor;
    ctx.textAlign = 'center';
    ctx.fillText(label, rightX - w / 2, headerY - 7);
  }
  ctx.textAlign = 'left';

  // Divider
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(PAD, 100, CARD_W - PAD * 2, 1);

  // ---- Headline ----
  ctx.fillStyle = COLORS.white;
  ctx.font = `700 46px ${SANS}`;
  const headlineLines = wrapText(ctx, payload.headline, CARD_W - PAD * 2, 2);
  let hy = 168;
  for (const line of headlineLines) {
    ctx.fillText(line, PAD, hy);
    hy += 56;
  }

  // ---- Section status panels ----
  const sections = (payload.sections || []).slice(0, 5);
  if (sections.length) {
    const gap = 14;
    const panelY = 300;
    const panelH = 96;
    const totalW = CARD_W - PAD * 2;
    const panelW = (totalW - gap * (sections.length - 1)) / sections.length;
    sections.forEach((sec, i) => {
      const px = PAD + i * (panelW + gap);
      ctx.fillStyle = COLORS.panel;
      roundRect(ctx, px, panelY, panelW, panelH, 6); ctx.fill();
      // colored status dot
      ctx.fillStyle = sec.color || COLORS.muted;
      ctx.beginPath();
      ctx.arc(px + 18, panelY + 26, 6, 0, Math.PI * 2);
      ctx.fill();
      // label
      ctx.fillStyle = COLORS.label;
      ctx.font = `700 14px ${SANS}`;
      ctx.fillText(sec.label.toUpperCase(), px + 32, panelY + 31);
      // status
      ctx.fillStyle = sec.color || COLORS.gray;
      ctx.font = `700 22px ${SANS}`;
      const status = (sec.status || '—').toUpperCase();
      let st = status;
      while (ctx.measureText(st).width > panelW - 36 && st.length > 1) st = st.slice(0, -1);
      ctx.fillText(st, px + 18, panelY + 70);
    });
  }

  // ---- Key stats ----
  const stats = (payload.stats || []).filter(s => s && s.value).slice(0, 4);
  if (stats.length) {
    const gap = 14;
    const rowY = 426;
    const rowH = 100;
    const totalW = CARD_W - PAD * 2;
    const cellW = (totalW - gap * (stats.length - 1)) / stats.length;
    stats.forEach((stat, i) => {
      const px = PAD + i * (cellW + gap);
      ctx.fillStyle = COLORS.panel;
      roundRect(ctx, px, rowY, cellW, rowH, 6); ctx.fill();
      ctx.fillStyle = COLORS.label;
      ctx.font = `700 13px ${SANS}`;
      ctx.fillText(stat.label.toUpperCase(), px + 18, rowY + 32);
      ctx.fillStyle = COLORS.white;
      ctx.font = `700 30px ${MONO}`;
      let v = String(stat.value);
      while (ctx.measureText(v).width > cellW - 36 && v.length > 1) v = v.slice(0, -1);
      ctx.fillText(v, px + 18, rowY + 74);
    });
  }

  // ---- Footer ----
  const footY = CARD_H - 42;
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(PAD, footY - 22, CARD_W - PAD * 2, 1);
  ctx.fillStyle = COLORS.orange;
  ctx.font = `700 20px ${SANS}`;
  ctx.textAlign = 'left';
  ctx.fillText('timechainstats.com', PAD, footY + 8);

  const footRight = [
    payload.blockHeight != null ? `Block #${Number(payload.blockHeight).toLocaleString()}` : null,
    payload.dateStr,
    payload.provider ? `via ${payload.provider}` : null
  ].filter(Boolean).join('   ·   ');
  ctx.fillStyle = COLORS.muted;
  ctx.font = `400 16px ${SANS}`;
  ctx.textAlign = 'right';
  ctx.fillText(footRight, CARD_W - PAD, footY + 8);
  ctx.textAlign = 'left';

  return canvas;
};

const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Build the card and share it. Tries the Web Share API (with file) first,
 * then falls back to a PNG download. Returns 'shared' | 'downloaded'.
 * Web fonts are awaited so the Ubuntu Mono numerals render when available.
 */
export const shareInsightCard = async (payload) => {
  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* non-fatal */ }
  }
  const canvas = renderShareCardCanvas(payload);
  const blob = await canvasToBlob(canvas);
  if (!blob) throw new Error('Could not render share image.');

  const filename = `timechainstats-ai-${payload.dateStr || 'insight'}.png`.replace(/\s+/g, '');
  const file = new File([blob], filename, { type: 'image/png' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: 'TimechainStats AI',
        text: payload.headline || 'Bitcoin network analysis from TimechainStats.com'
      });
      return 'shared';
    } catch (err) {
      // User cancelled the share sheet — don't fall through to a download
      if (err.name === 'AbortError') return 'shared';
    }
  }

  downloadBlob(blob, filename);
  return 'downloaded';
};
